import { useMemo, useState } from "react";
import { CurrencyText } from "@/components/common/text";
import { cn } from "@/utils/css";
import { pdfAt, sliderBounds } from "./priceDistribution";
import { RateBoundsLabels } from "./RateBoundsLabels";
import { ChartHoverTooltip } from "./ChartHoverTooltip";
import { rateScale } from "./useRateDistributionFit";

const CURVE_SAMPLES = 60;
const SNAP_RADIUS_PX = 3;
const VIEW_W = 314;
const VIEW_H = 53;
// The curve is bottom-aligned in a 60px box; the marker dot sits just above it.
const CURVE_TOP_PX = 7;
const MARKER_DOT_Y_PX = 10;

// Density curve of the market rate fit. Hover shows the price under the cursor;
// near the marker it snaps to the market rate.
export const MarketRateDensityCurve = ({ fit, markerRpm, priceScale, minPrice, maxPrice }) => {
    const [hover, setHover] = useState(null); // { rpm, y, snapped, width }

    const geometry = useMemo(() => {
        if (!fit) return null;
        const { lo, hi } = sliderBounds(fit);
        const scale = rateScale(lo, hi, VIEW_W);
        let maxDensity = 0;
        const samples = [];
        for (let i = 0; i <= CURVE_SAMPLES; i++) {
            const rpm = scale.sampleRpm(i, CURVE_SAMPLES);
            const d = fit.pointMass ? 0 : pdfAt(fit, rpm);
            samples.push({ rpm, d });
            if (d > maxDensity) maxDensity = d;
        }
        const toY = (d) => (maxDensity > 0 ? VIEW_H - (d / maxDensity) * (VIEW_H - 4) : VIEW_H);
        const path = [
            `M 0 ${VIEW_H}`,
            ...samples.map((s) => `L ${scale.toX(s.rpm).toFixed(2)} ${toY(s.d).toFixed(2)}`),
            `L ${VIEW_W} ${VIEW_H} Z`,
        ].join(" ");
        const markerFrac = markerRpm != null ? Math.min(1, Math.max(0, scale.toXFrac(markerRpm))) : null;
        return { scale, path, markerFrac, curveY: (rpm) => toY(pdfAt(fit, rpm)) };
    }, [fit, markerRpm]);

    if (!geometry) return null;
    const { pointMass } = fit;

    const handleMouseMove = (e) => {
        if (pointMass) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const { scale, markerFrac } = geometry;
        const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        // Snap only when the pointer is on the marker dot itself.
        const dx = markerFrac != null ? (frac - markerFrac) * rect.width : Infinity;
        const dy = e.clientY - rect.top - MARKER_DOT_Y_PX;
        const snapped = dx * dx + dy * dy <= SNAP_RADIUS_PX * SNAP_RADIUS_PX;
        if (snapped) {
            if (!hover?.snapped) setHover({ rpm: markerRpm, y: MARKER_DOT_Y_PX, snapped: true, width: rect.width });
            return;
        }
        const rpm = scale.lo + frac * (scale.hi - scale.lo);
        setHover({ rpm, y: CURVE_TOP_PX + geometry.curveY(rpm), snapped: false, width: rect.width });
    };

    const hoverX = hover ? geometry.scale.toXFrac(hover.rpm) : null;

    return (
        <div
            className={cn("relative h-[60px] w-full", pointMass && "opacity-40")}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHover(null)}
        >
            <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                preserveAspectRatio="none"
                className="absolute inset-x-0 bottom-0 h-[53px] w-full text-cm-p-surface-container"
            >
                <path d={geometry.path} fill="currentColor" />
                {geometry.markerFrac != null ? (
                    <line
                        x1={geometry.markerFrac * VIEW_W}
                        y1="0"
                        x2={geometry.markerFrac * VIEW_W}
                        y2={VIEW_H}
                        className="stroke-cm-p-outline"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                    />
                ) : null}
                {hover && !hover.snapped ? (
                    <line
                        x1={hoverX * VIEW_W}
                        y1={hover.y - CURVE_TOP_PX}
                        x2={hoverX * VIEW_W}
                        y2={VIEW_H}
                        className="stroke-cm-p-outline"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                    />
                ) : null}
            </svg>
            {geometry.markerFrac != null && (!hover || hover.snapped) ? (
                <div
                    className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cm-p-text-secondary"
                    style={{ left: `${geometry.markerFrac * 100}%`, top: MARKER_DOT_Y_PX }}
                />
            ) : null}
            {hover && !hover.snapped ? (
                <div
                    className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cm-p-text-secondary"
                    style={{ left: `${hoverX * 100}%`, top: hover.y }}
                />
            ) : null}
            <div className="absolute inset-x-0 top-0">
                <RateBoundsLabels leftLabel="Min" rightLabel="Max" minValue={minPrice} maxValue={maxPrice} emptyDash />
            </div>
            {hover ? (
                <ChartHoverTooltip x={hoverX * hover.width} y={hover.y} containerWidth={hover.width}>
                    <div className="flex flex-row items-center gap-2 leading-normal">
                        {priceScale != null ? (
                            <CurrencyText
                                num={hover.rpm * priceScale}
                                round
                                className="text-sm font-medium leading-normal text-cm-p-text-primary"
                            />
                        ) : null}
                        <span className="text-xs/[18px] text-cm-p-text-secondary">{hover.rpm.toFixed(2)}/mi</span>
                    </div>
                </ChartHoverTooltip>
            ) : null}
        </div>
    );
};